import { useEffect, useMemo, useRef } from 'react';

type TransitionKey = string | number | symbol;
type StateOrNull<S extends TransitionKey> = S | null;

type ConditionMap<S extends TransitionKey> = Record<S, () => boolean>;
type TransitionHandlerMap<S extends TransitionKey> = Partial<Record<S, () => void>>;

export function resolveState<S extends TransitionKey>(conditions: ConditionMap<S>): StateOrNull<S> {
    for (const key of Object.keys(conditions) as S[]) {
        if (conditions[key]()) return key;
    }
    return null;
}

export function useTransition<S extends TransitionKey>({
    state,
    callbacks,
}: {
    state: StateOrNull<S>;
    callbacks?: TransitionHandlerMap<S>;
}) {
    const lastStateRef = useRef<StateOrNull<S>>(null);

    useEffect(() => {
        if (state === lastStateRef.current) return;

        lastStateRef.current = state;

        if (state != null) {
            callbacks?.[state]?.();
        }
    }, [state, callbacks]);

    return {
        state,
        previousState: lastStateRef.current,
    };
}

export function useStateTransitions<S extends TransitionKey>({
    conditions,
    callbacks,
}: {
    conditions: ConditionMap<S>;
    callbacks?: TransitionHandlerMap<S>;
}) {
    const state = useMemo(() => resolveState(conditions), [conditions]);

    return useTransition<S>({
        state,
        callbacks,
    });
}
